import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MapApiService } from './map-api.service';
import { Category, MapEdge, MapNode, MapResponse, Trail } from './models';

interface ViewBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface LayerOption {
  id: string;
  label: string;
}

interface CategoryIsland {
  id: string;
  name: string;
  color: string;
  x: number;
  y: number;
  rx: number;
  ry: number;
}

@Component({
  selector: 'gam-root',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css'
})
export class AppComponent {
  private readonly api = inject(MapApiService);

  readonly layerOptions: LayerOption[] = [
    { id: 'all', label: 'Todos' },
    { id: 'civilizations', label: 'Civilizacoes' },
    { id: 'religions', label: 'Religioes' },
    { id: 'symbols', label: 'Simbolos' },
    { id: 'occult', label: 'Ocultismo' },
    { id: 'science', label: 'Ciencia' },
    { id: 'geopolitics', label: 'Geopolitica' },
    { id: 'mysteries', label: 'Misterios' }
  ];

  readonly data = signal<MapResponse | null>(null);
  readonly selectedNodeId = signal('n-great-awakening');
  readonly focusedNodeId = signal<string | null>(null);
  readonly query = signal('');
  readonly activeLayer = signal('all');
  readonly mapMode = signal<'explore' | 'total'>('explore');
  readonly deepMode = signal(false);
  readonly expandedConnections = signal(false);
  readonly enabledCategories = signal<Set<string>>(new Set());
  readonly viewBox = signal<ViewBox>({ x: -680, y: -460, width: 1360, height: 920 });
  readonly activeContext = signal('Um atlas navegavel em portugues sobre o despertar, simbolos e conexoes.');

  private dragging = false;
  private lastPointer: { x: number; y: number } | null = null;

  readonly selectedNode = computed(() => {
    const data = this.data();
    return data?.nodes.find((node) => node.id === this.selectedNodeId()) ?? data?.nodes[0] ?? null;
  });

  readonly activeDetailNode = computed(() => {
    if (!this.focusedNodeId()) {
      return null;
    }

    return this.selectedNode();
  });

  readonly visibleNodes = computed(() => {
    const data = this.data();
    const query = this.query().trim().toLowerCase();
    const enabled = this.enabledCategories();
    const focusedId = this.focusedNodeId();
    const selectedId = this.selectedNodeId();
    const mode = this.mapMode();
    const activeLayer = this.activeLayer();
    const connectedIds = focusedId ? this.contextNodeIds(selectedId, this.expandedConnections()) : new Set<string>();

    if (!data) {
      return [];
    }

    return data.nodes.filter((node) => {
      const category = this.categoryById(node.categoryId);
      const text = [node.title, node.titleOriginal, node.type, node.summary, node.description, category?.name]
        .join(' ')
        .toLowerCase();
      const matchesText = !query || text.includes(query);
      const matchesLayer = activeLayer === 'all' || this.nodeLayer(node) === activeLayer;

      if (!enabled.has(node.categoryId) || !matchesLayer || !matchesText) {
        return false;
      }

      if (mode === 'total' || query) {
        return true;
      }

      if (focusedId) {
        return connectedIds.has(node.id);
      }

      return this.isMainNode(node);
    });
  });

  readonly categoryIslands = computed(() => {
    if (this.focusedNodeId()) {
      return [];
    }

    const nodesByCategory = new Map<string, MapNode[]>();
    for (const node of this.visibleNodes()) {
      const group = nodesByCategory.get(node.categoryId) ?? [];
      group.push(node);
      nodesByCategory.set(node.categoryId, group);
    }

    return Array.from(nodesByCategory.entries())
      .filter(([, nodes]) => nodes.length >= 2)
      .map(([categoryId, nodes]) => {
        const category = this.categoryById(categoryId);
        const xs = nodes.map((node) => node.position.x);
        const ys = nodes.map((node) => node.position.y);
        const minX = Math.min(...xs);
        const maxX = Math.max(...xs);
        const minY = Math.min(...ys);
        const maxY = Math.max(...ys);
        const padding = this.mapMode() === 'explore' ? 260 : 180;

        return {
          id: categoryId,
          name: category?.name ?? categoryId,
          color: category?.color ?? '#f3b24a',
          x: (minX + maxX) / 2,
          y: (minY + maxY) / 2,
          rx: Math.max(210, (maxX - minX) / 2 + padding),
          ry: Math.max(150, (maxY - minY) / 2 + padding)
        };
      });
  });

  readonly degreeByNode = computed(() => {
    const degrees = new Map<string, number>();
    const data = this.data();

    if (!data) {
      return degrees;
    }

    for (const edge of data.edges) {
      degrees.set(edge.source, (degrees.get(edge.source) ?? 0) + 1);
      degrees.set(edge.target, (degrees.get(edge.target) ?? 0) + 1);
    }

    return degrees;
  });

  readonly visibleEdges = computed(() => {
    const data = this.data();
    const ids = new Set(this.visibleNodes().map((node) => node.id));
    const focusedId = this.focusedNodeId();
    const selectedId = this.selectedNodeId();
    const mode = this.mapMode();
    const deep = this.deepMode();

    return data?.edges.filter((edge) => {
      if (!ids.has(edge.source) || !ids.has(edge.target)) {
        return false;
      }

      if (mode === 'total') {
        return deep || edge.strength >= 3;
      }

      if (focusedId) {
        const direct = edge.source === selectedId || edge.target === selectedId;
        return direct || (this.expandedConnections() && edge.strength >= 4);
      }

      return edge.strength >= 4;
    }) ?? [];
  });

  readonly renderedEdges = computed(() => {
    return this.visibleEdges();
  });

  readonly relatedEdges = computed(() => {
    const data = this.data();
    const selected = this.selectedNode();

    if (!data || !selected) {
      return [];
    }

    return data.edges.filter((edge) => edge.source === selected.id || edge.target === selected.id);
  });

  readonly shouldShowEdgeLabels = computed(() => this.focusedNodeId() !== null && this.viewBox().width < 1200);

  ngOnInit(): void {
    this.api.getMap().subscribe((data) => {
      this.data.set(data);
      this.enabledCategories.set(new Set(data.categories.map((category) => category.id)));
      setTimeout(() => this.resetView());
    });
  }

  categoryById(id: string): Category | undefined {
    return this.data()?.categories.find((category) => category.id === id);
  }

  nodeById(id: string): MapNode | undefined {
    return this.data()?.nodes.find((node) => node.id === id);
  }

  edgePath(edge: MapEdge): string {
    const source = this.nodeById(edge.source);
    const target = this.nodeById(edge.target);

    if (!source || !target) {
      return '';
    }

    const dx = target.position.x - source.position.x;
    const curve = Math.max(-90, Math.min(90, dx * 0.18));
    const midY = (source.position.y + target.position.y) / 2 + curve;

    return `M ${source.position.x} ${source.position.y} Q ${(source.position.x + target.position.x) / 2} ${midY} ${target.position.x} ${target.position.y}`;
  }

  edgeLabelPosition(edge: MapEdge): { x: number; y: number } {
    const source = this.nodeById(edge.source);
    const target = this.nodeById(edge.target);

    return {
      x: ((source?.position.x ?? 0) + (target?.position.x ?? 0)) / 2,
      y: ((source?.position.y ?? 0) + (target?.position.y ?? 0)) / 2 - 8
    };
  }

  edgeClass(edge: MapEdge): string {
    const selectedId = this.selectedNodeId();
    const touchesSelected = edge.source === selectedId || edge.target === selectedId;
    return touchesSelected ? 'edge is-related' : 'edge';
  }

  nodeRadius(node: MapNode): number {
    const farScale = this.viewBox().width > 3600 ? 0.72 : this.viewBox().width > 2200 ? 0.86 : 1;
    return (18 + node.importance * 5) * farScale;
  }

  shouldShowNodeLabel(node: MapNode): boolean {
    const width = this.viewBox().width;

    if (node.id === this.selectedNodeId() || this.focusedNodeId() === node.id) {
      return true;
    }

    if (this.mapMode() === 'explore' && !this.focusedNodeId()) {
      return node.importance >= 4 || this.nodeDegree(node.id) >= 8;
    }

    if (width > 4200) {
      return node.importance >= 5;
    }

    if (width > 2600) {
      return node.importance >= 4;
    }

    if (width > 1600) {
      return node.importance >= 3;
    }

    return true;
  }

  setLayer(layerId: string): void {
    this.activeLayer.set(layerId);
    this.focusedNodeId.set(null);
    setTimeout(() => this.resetView());
  }

  setMode(mode: 'explore' | 'total'): void {
    this.mapMode.set(mode);
    if (mode === 'explore') {
      this.deepMode.set(false);
    }
    this.focusedNodeId.set(null);
    this.expandedConnections.set(false);
    setTimeout(() => this.resetView());
  }

  toggleDeepMode(): void {
    this.deepMode.set(!this.deepMode());
  }

  toggleExpandedConnections(): void {
    this.expandedConnections.set(!this.expandedConnections());
  }

  iconKind(node: MapNode): string {
    if (node.type.includes('archive')) return 'book';
    if (node.type.includes('finance') || node.id.includes('gold') || node.id.includes('crypto')) return 'coin';
    if (node.type.includes('portal') || node.id.includes('stargate')) return 'portal';
    if (node.type.includes('planet') || node.type.includes('moon') || node.id.includes('saturn') || node.id.includes('venus')) return 'planet';
    if (node.type.includes('people') || node.type.includes('group') || node.type.includes('alliance')) return 'people';
    if (node.type.includes('substance')) return 'drop';
    if (node.type.includes('operation') || node.type.includes('program')) return 'shield';
    if (node.type.includes('teaching') || node.type.includes('theory') || node.type.includes('framework')) return 'book';
    if (node.type.includes('technology') || node.type.includes('device')) return 'gear';
    if (node.type.includes('time') || node.id.includes('timeline') || node.id.includes('travel')) return 'hourglass';
    if (node.type.includes('field') || node.id.includes('schumann')) return 'wave';
    if (node.type.includes('location')) return 'pin';
    if (node.type.includes('person')) return 'person';
    if (node.type.includes('beings') || node.type.includes('entity')) return 'being';
    if (node.type.includes('healing') || node.id.includes('organic')) return 'leaf';
    if (node.id.includes('reptilian')) return 'reptile';
    if (node.id.includes('solar') || node.id.includes('flash')) return 'sun';
    if (node.id.includes('meditation') || node.id.includes('dzogchen') || node.id.includes('rainbow')) return 'lotus';
    if (node.id.includes('space') || node.id.includes('warden') || node.id.includes('stargates')) return 'ship';
    if (node.id.includes('antarctica') || node.id.includes('earth')) return 'mountain';
    if (node.id.includes('ai') || node.id.includes('matrix')) return 'chip';
    if (node.id.includes('tesla') || node.id.includes('energy') || node.id.includes('wireless')) return 'bolt';
    if (node.id.includes('cabal') || node.id.includes('q-anon') || node.id.includes('mkultra')) return 'eye';
    if (node.categoryId === 'esoteric-practice' || node.categoryId === 'source') return 'spark';
    if (node.categoryId === 'ancient-builder') return 'pyramid';
    return 'orb';
  }

  toggleCategory(categoryId: string, checked: boolean): void {
    const next = new Set(this.enabledCategories());
    checked ? next.add(categoryId) : next.delete(categoryId);
    this.enabledCategories.set(next);
    if (!this.focusedNodeId()) {
      setTimeout(() => this.resetView());
    }
  }

  selectNode(nodeId: string): void {
    const node = this.nodeById(nodeId);

    if (node) {
      this.selectedNodeId.set(nodeId);
      const category = this.categoryById(node.categoryId);
      this.activeContext.set(`${category?.name ?? 'Mapa'} / ${node.type}`);

      if (this.focusedNodeId() === nodeId) {
        this.focusedNodeId.set(null);
        this.resetView();
        return;
      }

      this.focusedNodeId.set(nodeId);
      this.expandedConnections.set(false);
      this.focusNode(node);
    }
  }

  startTrail(trail: Trail): void {
    const first = trail.steps[0];
    if (!first) {
      return;
    }

    this.selectNode(first.nodeId);
    this.activeContext.set(trail.description);
  }

  zoom(multiplier: number): void {
    const box = this.viewBox();
    const centerX = box.x + box.width / 2;
    const centerY = box.y + box.height / 2;
    const width = Math.max(420, Math.min(7600, box.width * multiplier));
    const height = Math.max(300, Math.min(5200, box.height * multiplier));

    this.viewBox.set({
      x: centerX - width / 2,
      y: centerY - height / 2,
      width,
      height
    });
  }

  resetView(): void {
    this.focusedNodeId.set(null);
    this.expandedConnections.set(false);
    this.viewBox.set(this.fitViewBoxToNodes(this.visibleNodes()));
    this.activeContext.set('Visao geral do AwakenMap');
  }

  closeDetail(): void {
    this.focusedNodeId.set(null);
  }

  /**
   * Aproxima o mapa no assunto escolhido.
   *
   * A regra de produto e simples: primeiro clique aproxima; segundo clique no
   * mesmo assunto volta ao mapa geral. Mantemos isso aqui para facilitar seu
   * estudo depois.
   */
  private focusNode(node: MapNode): void {
    const width = 1120;
    const height = 780;

    this.viewBox.set({
      x: node.position.x - width / 2,
      y: node.position.y - height / 2,
      width,
      height
    });
  }

  /**
   * Calcula a visao geral a partir dos pontos reais.
   *
   * Antes o mapa geral usava um viewBox fixo, entao alguns filtros podiam
   * deixar pontos fora da area confortavel. Agora enquadramos todos os pontos
   * visiveis com uma margem, como um "zoom para caber".
   */
  private fitViewBoxToNodes(nodes: MapNode[]): ViewBox {
    if (!nodes.length) {
      return { x: -1200, y: -820, width: 2400, height: 1640 };
    }

    const xs = nodes.map((node) => node.position.x);
    const ys = nodes.map((node) => node.position.y);
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    const padding = 360;
    const width = Math.max(2200, maxX - minX + padding * 2);
    const height = Math.max(1500, maxY - minY + padding * 2);

    return {
      x: minX - padding,
      y: minY - padding,
      width,
      height
    };
  }

  pointerDown(event: PointerEvent): void {
    event.preventDefault();
    const canvas = event.currentTarget as SVGSVGElement;
    this.dragging = true;
    this.lastPointer = this.pointerToViewBox(event, canvas);
    canvas.setPointerCapture(event.pointerId);
  }

  pointerMove(event: PointerEvent): void {
    if (!this.dragging || !this.lastPointer) {
      return;
    }

    event.preventDefault();
    const canvas = event.currentTarget as SVGSVGElement;
    const point = this.pointerToViewBox(event, canvas);
    const box = this.viewBox();

    this.viewBox.set({
      ...box,
      x: box.x - (point.x - this.lastPointer.x),
      y: box.y - (point.y - this.lastPointer.y)
    });
  }

  pointerUp(event: PointerEvent): void {
    const canvas = event.currentTarget as SVGSVGElement;
    this.dragging = false;
    this.lastPointer = null;
    canvas.releasePointerCapture(event.pointerId);
  }

  wheel(event: WheelEvent): void {
    event.preventDefault();
    this.zoom(event.deltaY > 0 ? 1.1 : 0.9);
  }

  relatedNode(edge: MapEdge): MapNode | undefined {
    const selected = this.selectedNode();
    const relatedId = edge.source === selected?.id ? edge.target : edge.source;
    return this.nodeById(relatedId);
  }

  private isMainNode(node: MapNode): boolean {
    if (node.categoryId === 'core') {
      return true;
    }

    return node.importance >= 4 || this.nodeDegree(node.id) >= 6;
  }

  private nodeDegree(nodeId: string): number {
    return this.degreeByNode().get(nodeId) ?? 0;
  }

  private contextNodeIds(nodeId: string, expanded: boolean): Set<string> {
    const data = this.data();
    const ids = new Set<string>([nodeId]);

    if (!data) {
      return ids;
    }

    const direct = data.edges.filter((edge) => edge.source === nodeId || edge.target === nodeId);
    for (const edge of direct) {
      ids.add(edge.source === nodeId ? edge.target : edge.source);
    }

    if (expanded) {
      const firstRing = new Set(ids);
      for (const edge of data.edges) {
        if (edge.strength < 4) {
          continue;
        }

        if (firstRing.has(edge.source)) {
          ids.add(edge.target);
        }

        if (firstRing.has(edge.target)) {
          ids.add(edge.source);
        }
      }
    }

    return ids;
  }

  private nodeLayer(node: MapNode): string {
    const searchable = `${node.title} ${node.titleOriginal ?? ''} ${node.type} ${node.summary}`.toLowerCase();

    if (node.categoryId === 'ancient-builder' || node.categoryId === 'earth-antarctica') {
      return 'civilizations';
    }

    if (node.categoryId === 'source' || searchable.includes('bardo') || searchable.includes('creator')) {
      return 'religions';
    }

    if (node.categoryId === 'consciousness' || searchable.includes('geometry') || searchable.includes('merkaba')) {
      return 'symbols';
    }

    if (node.categoryId === 'esoteric-practice') {
      return 'occult';
    }

    if (node.categoryId === 'solar-flash' || node.categoryId === 'healing-energy' || node.categoryId === 'ai-matrix') {
      return 'science';
    }

    if (node.categoryId === 'control-systems') {
      return 'geopolitics';
    }

    if (node.categoryId === 'ssp') {
      return 'mysteries';
    }

    return 'mysteries';
  }

  private pointerToViewBox(event: PointerEvent, canvas: SVGSVGElement): { x: number; y: number } {
    const rect = canvas.getBoundingClientRect();
    const box = this.viewBox();

    return {
      x: ((event.clientX - rect.left) / rect.width) * box.width + box.x,
      y: ((event.clientY - rect.top) / rect.height) * box.height + box.y
    };
  }

  trackNode(_index: number, node: MapNode): string {
    return node.id;
  }

  trackEdge(_index: number, edge: MapEdge): string {
    return edge.id;
  }

  trackIsland(_index: number, island: CategoryIsland): string {
    return island.id;
  }
}
